// Порт эталонной дизайн-системы (Figma «OA Studio App»): пять подложек. Реф
// (renderBackdropSelector:275-339) предлагал автору только четыре — `blur`
// жил только в типе, здесь она полноценная опция «Контур».
import { Label, cn } from '@teach/ui'
import type { DesignBackdrop } from '@/types/blocks'
import {
  backdropHasFill,
  backdropStyle,
  backdropTextColor,
} from '@/features/editor/lib/designElementStyles'
import { segmentedButtonClass } from './segmented'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { TransparentFrame } from '../previews/design/TransparentFrame'

interface Props {
  value: DesignBackdrop
  onSelect: (value: DesignBackdrop) => void
}

type BackdropKey =
  'backdropNone' | 'backdropLight' | 'backdropDark' | 'backdropPrimary' | 'backdropBlur'

const BACKDROP_KEYS: Array<{ value: DesignBackdrop; key: BackdropKey }> = [
  { value: 'none', key: 'backdropNone' },
  { value: 'light', key: 'backdropLight' },
  { value: 'dark', key: 'backdropDark' },
  { value: 'primary', key: 'backdropPrimary' },
  { value: 'blur', key: 'backdropBlur' },
]

export const BackdropPicker = ({ value, onSelect }: Props) => {
  const m = useMessages(messages)
  const BACKDROPS = BACKDROP_KEYS.map(({ value: v, key }) => ({ value: v, label: m[key] }))
  return (
    <div className="space-y-3">
      <Label className="text-xs text-muted-foreground">{m.backdrop}</Label>
      {/* Регрессия предыдущей волны: flex-wrap решал переполнение (замер: scrollWidth
        355 против clientWidth 346 у панели 380px — «Прозрачная», самое длинное
        слово, не влезала в свою 1/5 колонку grid-cols-5), но сломал соответствие
        колонок ряду плашек-превью ниже (тот остался на grid-cols-5) — подпись
        «Тёмная» больше не стояла над своей плашкой. Оба ряда — независимые контролы
        ОДНОГО значения, совпадение колонок между ними — единственное, что их
        связывает. Возвращаем grid-cols-5 (тот же, что у плашек), а переполнение
        снимаем `break-words`: «Прозрачная» — один токен без пробелов, без
        break-words браузер не переносит слово внутри узкой ячейки (это и вызывало
        overflow), а grid-cols-5 сам по себе (minmax(0, 1fr)) сжатию не мешает. */}
      <div className="grid grid-cols-5 gap-1">
        {BACKDROPS.map(({ value: v, label }) => (
          <button
            key={v}
            type="button"
            onClick={() => onSelect(v)}
            className={cn('break-words px-1 py-1.5 text-xs', segmentedButtonClass(value === v))}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {BACKDROPS.map(({ value: v, label }) => (
          <button
            key={v}
            type="button"
            onClick={() => onSelect(v)}
            aria-label={m.backdropAria(label)}
            style={backdropStyle(v, true)}
            className={cn(
              // Индикатор выбора — `outline`, а не `border`/`ring`: оба уже пробовали,
              // и оба перебивались инлайновым стилем подложки (`backdropStyle` кладёт
              // либо `border`, либо `boxShadow` на этот же элемент — `ring` в Tailwind
              // это тоже `box-shadow`). `outline` — отдельное CSS-свойство, инлайновый
              // стиль подложки его не трогает и не тронет, какие бы тени ни добавили.
              'relative flex h-10 items-center justify-center rounded-lg outline outline-2 outline-offset-2 transition-all',
              value === v ? 'outline-primary' : 'outline-transparent hover:outline-border',
            )}
          >
            {/* Рамка прозрачной подложки — отдельный слой, как на холсте. */}
            {v === 'blur' ? <TransparentFrame rounded /> : null}
            {/* У `none` и `blur` заливки нет (см. backdropHasFill) — плашка стоит
              прозрачной поверх ТЁМНОЙ панели студии, а не светлого канваса урока.
              `backdropTextColor` красит текст под канвас (`#202020`), на тёмной
              панели он невидим — здесь нужен тематический цвет интерфейса,
              а не цвет из палитры контента. */}
            <span
              style={backdropHasFill(v) ? { color: backdropTextColor(v) } : undefined}
              className={cn(
                'text-[8px] font-medium opacity-60',
                backdropHasFill(v) ? null : 'text-foreground',
              )}
            >
              Aa
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
